import { useState, useRef } from 'react';
import { gtmPushEvent, sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import translator from '@Components/Utilities/Translator';
import AVBanner from '@Components/Shared/AVBanner';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { setCookie } from '@Components/Utilities/CookieHelper';
import { cookieNames } from '@Components/Utilities/SandhillsConstants';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';

const CheckboxGrid = styled(Grid)({
    display: 'flex',
    alignItems: 'center'
});

const CheckboxField = styled(Checkbox)({
    alignSelf: 'flex-start',
    padding: '0px',
    width: '18px',
    height: '18px',
    position: 'relative',

    '& .MuiSvgIcon-root': {
        display: 'none'
    },

    '&::before': {
        content: '""',
        display: 'block',
        width: '16px',
        height: '16px',
        border: '1px solid #000',
        borderRadius: '2px',
        boxSizing: 'border-box'
    },

    '&.Mui-checked::after': {
        content: '""',
        position: 'absolute',
        top: '2px',
        left: '6px',
        width: '5px',
        height: '9px',
        border: 'solid #000',
        borderWidth: '0 2px 2px 0',
        transform: 'rotate(45deg)'
    }
});

const MarketReportImage = styled(Box)({
    marginRight: '15px',
    width: '64px',
    height: '47px',
});

const MarketListItem = styled(ListItem)({
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    fontSize: '14px',
    padding: '0'
});

const TypographyText = styled(Typography)({
    fontSize: '14px'
});

const TypographyError = styled(TypographyText)(({ theme }) => ({
    color: theme.palette.error.main
}));

const SpanError = styled('span')(({ theme }) => ({
    alignSelf: 'flex-start',
    color: theme.palette.error.main
}));

const MarketReportView = (props) => {
    const mediaDesktopMatch = useMediaQuery('(min-width:1001px)');
    const mediaMobileMatch = useMediaQuery('(max-width:1000px)');
    const [showSuccess, setSuccess] = useState(props.Success);
    const [validationErrors, setValidationErrors] = useState({});
    const [disableSubmit, setDisableSubmit] = useState(false);
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const inputReferences = useRef({});
    const [formData, setFormData] = useState({
        Email: props.EnteredEmailAddress || '',
        PhonePlaceholder: props.PhonePlaceholder || '',
        Phone: props.Phone || '',
        Business: props.Business || '',
        Industry: currentSite.Site.IsRVUniverse ? Object.entries(props.Industries)[0][1] : props.Industry || '',
        EmailSubAndAcknowledgement: props.EmailSubAndAcknowledgement || {},
    });

    const onIndustryCheckboxChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        let prevValue = formData.Industry;
        if (prevValue.includes(value)) {
            prevValue = prevValue.replace((value + ','), '');
            value = '';
        }
        setFormData(previousState => {
            return { ...previousState, [name]: value + ',' + prevValue };
        });
    };

    const onLegalCheckboxChange = (e) => {
        const checked = e.target.checked;
        const stateName = e.target.id;
        setFormData(previousState => ({
            ...previousState,
            EmailSubAndAcknowledgement: {
                ...previousState.EmailSubAndAcknowledgement,
                [stateName]: checked
            }
        }));

        setValidationErrors(previousState => {
            return { ...previousState, [stateName]: '' };
        });
    };

    const onTextChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const submitForm = (e) => {
        e.preventDefault();

        let errors = run(formData, [
            ruleRunner('Email', 'Email', ValidationRules.validEmail),
            ruleRunner('Phone', 'Phone', ValidationRules.validPhone),
            ruleRunner('Business', 'Business', ValidationRules.required),
            ruleRunner('Industry', 'Industry', ValidationRules.required)
        ]);

        if (!formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted && formData.EmailSubAndAcknowledgement.WeeklyUpdateVisible) {
            errors.SubscribeToWeeklyUpdate = translator.translate('sandhills.required').toString();
        }

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            onSubmit();
        }
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.MarketReport,
            method: 'post',
            data: {
                Email: formData.Email,
                PhonePlaceholder: formData.PhonePlaceholder,
                Phone: formData.Phone,
                Business: formData.Business,
                Industry: formData.Industry,
                EmailSubAndAcknowledgement: {
                    PrivacyPolicyAccepted: formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted,
                    PixelsPolicyAccepted: formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted
                },
                Captcha: {
                    Response: token
                },
				WebAgreementTypeIDs: props.WebAgreementTypeIDs
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response.Success) {
                gtmPushEvent('market-report-form');
            } else {
                setDisableSubmit(false);
                setValidationErrors(response.ValidationErrors);
                focusErroredField(response.ValidationErrors, inputReferences);
            }
            if (!currentUser.IsAuthenticated) {
                let date = new Date;
                date.setTime(date.getTime() + 5 * 60 * 1000);
                let expiresOn = date.toUTCString();
                setCookie(cookieNames.marketReportEmail, formData.Email, expiresOn);
                window.location.href = routes.SSO + '?to=' + encodeURIComponent(window.location.href + '?Business=' + formData.Business + '&Industries=' + formData.Industry + '&TermsConsent=' + formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted + '&PixelsConsent=' +formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted);
                
            }
            setFormData(previousState => ({
                ...previousState,
                Email: response.Email || '',
                Business: response.Business || '',
                Industry: response.Industry || '',
                EmailSubAndAcknowledgement: {
                    ...previousState.EmailSubAndAcknowledgement,
                    SubscribeToWeeklyUpdate: previousState.EmailSubAndAcknowledgement?.SubscribeToWeeklyUpdate ?? false,
                    PrivacyPolicyAccepted: previousState.EmailSubAndAcknowledgement?.PrivacyPolicyAccepted ?? false,
                    PixelsPolicyAccepted: previousState.EmailSubAndAcknowledgement?.PixelsPolicyAccepted ?? false
                }
            }));
            setSuccess(response.Success);
        });
    };

    return (
        <Box sx={{
            fontFamily: 'Roboto, sans-serif',
        }} className='signup-container'>
            <Box sx={{
                background: 'url(/Content/images/marketreport/bg.jpg) no-repeat center center',
                width: '100%'
            }} className='signup-title'>
                <Box sx={{
                    maxWidth: '1124px',
                    margin: '0 auto',
                    alignItems: mediaDesktopMatch ? 'center' : 'start',
                    display: mediaDesktopMatch ? 'flex': 'block',
                }} className='signup-info'>
                    <Box sx={{
                        color: '#ffffff',
                        textAlign: mediaMobileMatch ? 'center' : 'left',
                        margin: mediaMobileMatch ? '0 10px' : '0 25px 0 0', 
                    }} className='signup-text'>
                        <Typography variant="h1" sx={{
                            textTransform: 'uppercase',
                            fontSize: mediaDesktopMatch ? '40px' : '30px',
                            fontWeight: '700',
                            lineHeight: '1',
                            margin: '0 0 20px',
                            paddingTop: '20px',
                            color: 'inherit'
                        }}>{(translator.translate('sandhills.tradesites.mvc.dynamicmarketreporttitle').replace('{0}', props.EquipmentName))}</Typography>
                        <Typography sx={{
                            fontSize: mediaDesktopMatch ? '20px' : '17px',
                            lineHeight: '1.3',
                            color: '#D7E2EF',
                            marginBottom: '20px',
                        }}>{(translator.translate('sandhills.tradesites.mvc.pricingdatatoyourinbox').replace('{0}', props.SecondaryEquipmentName ?? props.EquipmentName))}</Typography>
                    </Box>
                    <Box sx={{
                        display: mediaMobileMatch ? 'block' : 'flex',
                        position: mediaDesktopMatch ? 'relative' : '',
                        width: mediaDesktopMatch ? '500px' : '100%',
                        marginLeft: mediaDesktopMatch ? 'auto' : '',
                        marginBottom: '10px',
                        top: mediaDesktopMatch ? '35px' : '',
                    }} className='signup-img'>
                        <Box
                            sx={{
                                display: 'block',
                                maxWidth: mediaMobileMatch ? '60%' : '500px',
                                margin: mediaMobileMatch ? 'auto' : '',
                                paddingTop: mediaMobileMatch ? '10px' : '',
                            }}
                            component='img' src='/Content/images/marketreport/reports.png' alt='Market Report Signup'
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{
                maxWidth: '1124px',
                margin: '0 auto',
                marginBottom: '30px',
                alignItems: 'center',
                display: mediaDesktopMatch ? 'flex' : 'block',
                flexDirection: mediaDesktopMatch ? 'column' : '',
            }} className='signup-content-container'>
                <Box sx={{
                    display: mediaDesktopMatch ? 'flex' : '',
                }} className='signup-content'>
                <Box sx={{
                    width: mediaDesktopMatch ? '600px' : 'auto',
                    background: '#ffffff',
                    padding: '30px',
                    borderRadius: '10px',
                    boxShadow: '0 5px 15px 0 rgba(0, 0, 0, .1)',
                    margin: mediaMobileMatch ? '15px 10px 0 10px' : '25px 0 0 0'
                }} className='signup-form' onSubmit={submitForm}>
                    <ValidationErrors errors={validationErrors}/>
                    {showSuccess ?
                    <>
                        <Box sx={{
                            margin: '25% 0',
                            textAlign: 'center',
                        }} className='signup-thanks'>
                            <CheckCircleIcon sx={{
                                margin: '15px 0',
                                fontSize: '72px',
                                color: '#128528'
                            }} />
                            <Typography sx={{
                                fontSize: '24px',
                                fontWeight: '700',
                                lineHeight: '1',
                                margin: '0 0 25px'
                            }} variant="h2"><TranslationText tKey='sandhills.thankyou' /></Typography>
                            <Typography variant="body1">{(translator.translate('sandhills.tradesites.mvc.marketreportthankyou').replace('{0}', props.SiteName))}</Typography>
                        </Box>
                            <Button sx={{
                                color: '#ffffff',

                            }} href={ props.BreadCrumbHomeRoute } className='button ts-button' > <TranslationText tKey='sandhills.returnhome' /></Button>
                            </>
                            :
                            <>
                                <Typography variant='h2'><TranslationText tKey='sandhills.tradesites.mvc.signupformontlymarketreports' /></Typography>
                                <Box sx={{ margin: '20px 0' }} className='signup-row' >
                                    <fieldset id='checkboxes' className='ts__fieldset'>
                                    <legend className='require ts__legend'>{translator.translate('sandhills.tradesites.mvc.receivereportsfromindustries')}<span className='required'></span></legend>
                                        {props.Industries &&
                                            Object.keys(props.Industries).map(key =>
                                                <label htmlFor={key}>
                                                    <input type='checkbox' id={key} value={props.Industries[key]} name='Industry' onChange={onIndustryCheckboxChange} checked={formData.Industry.includes(props.Industries[key])} ref={(element) => inputReferences.current[props.Industries[key]] = element} />
                                                    {key}
                                                </label>)}
                                    </fieldset>
                                    {validationErrors && validationErrors.Industry &&
                                        <span className='error'>{validationErrors.Industry}</span>}
                                </Box>
                                <Box sx={{ margin: '20px 0' }} className='signup-row' >
                                    <label htmlFor='Business'><TranslationText tKey='sandhills.tradesites.mvc.typeofbusiness' />:<div className='required' /></label>
                                    <select onChange={onTextChange} id='Business' name='Business' ref={(element) => inputReferences.current[`Business`] = element}>
                                        <option value=''>{translator.translate('sandhills.tradesites.mvc.typeofbusiness')}</option>
                                        {props.AvailableBusinessTypes &&
                                            Object.keys(props.AvailableBusinessTypes).map(key =>
                                                <option value={props.AvailableBusinessTypes[key]} selected={formData.Business.includes(props.AvailableBusinessTypes[key])}>
                                                    {key}
                                                </option>)}
                                    </select>
                                    {validationErrors && validationErrors.Business &&
                                        <span className='error'>{validationErrors.Business}</span>}
                                </Box>
                                <Box sx={{ margin: '20px 0' }} className='signup-row' >
                                    <label htmlFor='Email'><TranslationText tKey='sandhills.emailaddress' /><div className='required' /></label>
                                    <TextField
                                        id='Email'
                                        name='Email'
                                        value={formData.Email}
                                        placeholder={translator.translate('sandhills.email')}
                                        type='text'
                                        class='modal-form-el'
                                        required={true}
                                        validationErrors={(validationErrors && validationErrors.Email ? validationErrors.Email : '')}
                                        ref={(element) => inputReferences.current[`Email`] = element}
                                        onFieldChanged={onTextChange}
                                    />
                                </Box>
                                <Box sx={{ margin: '20px 0' }} className='signup-row'>
                                    <TextField
                                        id='Phone'
                                        name='Phone'
                                        value={formData.Phone}
                                        placeholder={formData.PhonePlaceholder}
                                        type='text'
                                        label={translator.translate('sandhills.phonenumber')}
                                        labelClass='label'
                                        class='modal-form-el'
                                        required={true}
                                        validationErrors={validationErrors?.Phone}
                                        ref={(element) => inputReferences.current[`Phone`] = element}
                                        onFieldChanged={onTextChange}
                                    />
                                </Box>
                                {formData.EmailSubAndAcknowledgement && 
                                    <Box sx={{ margin: '20px 0' }} className='signup-row' >
                                        {formData.EmailSubAndAcknowledgement.WeeklyUpdateVisible &&
                                            <CheckboxGrid item xs={12}>
                                                <CheckboxField
                                                    size='small'
                                                    id='PrivacyPolicyAccepted'
                                                    className='registration-checkbox'
                                                    checked={formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted}
                                                    inputRef={element => inputReferences.current['PrivacyPolicyAccepted'] = element}
                                                    onChange={onLegalCheckboxChange}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            onLegalCheckboxChange({
                                                                target: {
                                                                    id: 'PrivacyPolicyAccepted',
                                                                                         checked: !formData.EmailSubAndAcknowledgement.PrivacyPolicyAccepted
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    inputProps={{
                                                        'aria-labelledby': 'sub-weekly-update',
                                                    }}
                                                />
                                                <TypographyText id='sub-weekly-update' sx={{ paddingLeft: '5px' }}>
                                                    <SpanError>* </SpanError><span dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PrivacyPolicyText }} />
                                                </TypographyText>
                                            </CheckboxGrid>}
                                        {formData.EmailSubAndAcknowledgement.PixelsPolicyStatementVisible &&
                                            <CheckboxGrid item xs={12} sx={{ paddingTop: '5px' }}>
                                                <CheckboxField
                                                    size='small'
                                                    id='PixelsPolicyAccepted'
                                                    className='registration-checkbox'
                                                    checked={formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted}
                                                    onChange={onLegalCheckboxChange}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            onLegalCheckboxChange({
                                                                target: {
                                                                    id: 'PixelsPolicyAccepted',
                                                                                         checked: !formData.EmailSubAndAcknowledgement.PixelsPolicyAccepted
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    inputProps={{
                                                        'aria-labelledby': 'pixels-policy-consent',
                                                    }}
                                                />
                                                <TypographyText id='pixels-policy-consent' dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.PixelsPolicyText }} sx={{ paddingLeft: '5px' }} />
                                            </CheckboxGrid>}
                                        {formData.EmailSubAndAcknowledgement.InternationalLegalStatement &&
                                            <TypographyText dangerouslySetInnerHTML={{ __html: formData.EmailSubAndAcknowledgement.InternationalLegalStatement }} sx={{ paddingTop: '5px' }} />}
                                    </Box>
                                }
                                <Button sx={{
                                    padding: '15px',
                                    textTransform: 'uppercase',
                                    fontSize: '20px',
                                    width: '100%',
                                    fontWeight: '700',
                                    lineHeight: 'inherit',
                                    border: 'none',
                                    margin: '5px 0px',
                                    color: '#ffffff',
                                    '&:focus-visible': {
                                        outline: '3px solid #000000',
                                        outlineOffset: '2px'
                                    }
                                }} id='submit' type='submit' onClick={submitForm}>{translator.translate('sandhills.tradesites.mvc.subscribeforfree')}</Button>
                                <Box sx={{
                                    fontSize: '.75em',
                                }} dangerouslySetInnerHTML={{ __html: props.CAPrivacyPolicyNotice }} style={{ 'font-size': '.75em' }} />
                                {disableSubmit && <LoadingRing backdrop />}
                            </>}
                    </Box>
                    <Box sx={{
                        padding: '30px',
                        width: mediaDesktopMatch ? 'calc(100% - 500px)' : '100%',
                    }} className='signup-details' >
                        <Typography variant="body1">{props.MonthlyReportDetails}</Typography>
                        <Typography sx={{ fontSize: '1.17em', marginBlockStart: '1em', marginBlockEnd: '1em', fontWeight: 'bold' }} variant="h3"><TranslationText tKey='sandhills.tradesites.mvc.marketreportshelpyou' /></Typography>
                        <List sx={{
                            border: '0',
                            margin: '0',
                            padding: '0',
                            listStyleType: 'none'
                        }}>
                            {props.BenefitItems.map((item) =>
                                <MarketListItem>
                                    <MarketReportImage component="img" src={item[0]} alt='' />
                                    <TranslationText tKey={item[1]} />
                                </MarketListItem>
                            )}
                        </List>
                    </Box>
                </Box>
                {props.ShowAVPlug && <AVBanner />}
            </Box>
        </Box>
    )
};

export default MarketReportView;
global.MarketReportView = MarketReportView;